import React, { forwardRef, useState } from 'react';
import { Collapse, Alert as MuiAlert, alertClasses as MuiAlertClasses, AlertColor } from '@mui/material';
import { CheckCircleOutline, Close as CloseIcon, MenuDown, MenuUp, CheckDecagramOutline } from 'mdi-material-ui';
import { SxProps, combineSx } from '../../utils';
import { IconButton } from '../IconButton';
import { Stack } from '../Stack';
import { Button } from '../Button';
import { Typography } from '../Typography';

type AlertSeverity = 'error' | 'info' | 'success' | 'warning' | 'new';

export interface AlertProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'color'> {
  /**
   * The content of the alert
   */
  children: React.ReactNode;
  /**
   * The action to display. It renders after the message, at the end of the alert
   */
  Action?: React.ReactNode;
  /**
   * Shadow depth, corresponds to dp in the spec. It accepts values between 0 and 24 inclusive.
   */
  elevation?: number;
  /**
   * A bolded title that renders above the children
   */
  Header?: React.ReactNode;
  /**
   * If true, the alert icon is hidden.
   * @default false
   */
  hideIcon?: boolean;
  /**
   * A collapsible section containing more information about the alert. It will only render if a value is passed in.
   */
  MoreDetails?: React.ReactNode;
  /**
   * Callback fired when the component requests to be closed.
   * When provided, a close icon button is displayed that triggers the callback when clicked.
   */
  onClose?: (event: React.SyntheticEvent) => void;
  /**
   * The severity of the alert. This defines the color and icon used
   * @default 'success'
   */
  severity?: AlertSeverity;
  /**
   * If true, rounded corners are disabled.
   * @default false
   */
  square?: boolean;
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps;
}

export const alertClasses = {
  message: MuiAlertClasses.message,
  action: MuiAlertClasses.action,
};

/*

Internal overrides for some icons that we are customizing outside of MUI (or for severity variants that don't exist there):

*/
const getIcon = (severity: AlertSeverity) => {
  if (severity === 'success') return <CheckCircleOutline />;
  if (severity === 'new') return <CheckDecagramOutline />;
};

/*

There is a bug in versions of MUI below 5.15.4 where the `severity` prop can't be overridden like `color` can be
and `AlertColor` is restricted to the 4 default options (info, warning, error, success).
https://github.com/mui/material-ui/pull/40551

Until we can upgrade, we need to extend the options outside of the component and proxy in a valid option to keep
the linter happy.

*/
const getMuiSeverity = (severity: AlertSeverity): AlertColor => {
  if (severity === 'new') return 'info';
  return severity;
};

export const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  { severity = 'success', hideIcon, square, Action, children, onClose, MoreDetails, Header, sx, ...props },
  ref
) {
  const [detailsVisible, setDetailsVisible] = useState(false);
  const icon = getIcon(severity);
  const ariaLabel = props['aria-label'];
  return (
    <MuiAlert
      sx={combineSx(
        {
          [`&.${MuiAlertClasses.root}`]: {
            alignItems: 'flex-start',
          },
          ...(severity === 'new' && {
            [`&.${MuiAlertClasses.standardInfo}`]: {
              color: (t) => t.palette.status.new.highContrast,
              backgroundColor: (t) => t.palette.status.new.lowContrast,
              [`.${MuiAlertClasses.icon}`]: {
                color: (t) => t.palette.status.new.highContrast,
              },
            },
          }),
        },
        sx
      )}
      severity={getMuiSeverity(severity)}
      square={square}
      {...props}
      ref={ref}
      icon={hideIcon ? false : icon}
      aria-label={ariaLabel}
      action={
        Action || onClose ? (
          // The 2px vertical margin is necessary to make the action buttons look centered when the
          // alert doesn't grow vertically
          <Stack direction="row" gap={1} my="2px">
            {Action}
            {onClose && (
              <IconButton aria-label={ariaLabel ? `close ${ariaLabel}` : `close alert`} onClick={onClose}>
                <CloseIcon />
              </IconButton>
            )}
          </Stack>
        ) : undefined
      }
    >
      {Header && (
        <Typography fontWeight="bold" variant="h4">
          {Header}
        </Typography>
      )}
      {children}
      {MoreDetails && (
        <Stack>
          <Button
            onClick={() => setDetailsVisible(!detailsVisible)}
            StartIcon={detailsVisible ? <MenuUp /> : <MenuDown />}
            variant="tertiary"
            size="small"
            sx={{ width: 'fit-content', padding: 0, fontSize: (theme) => theme.typography.body2, fontWeight: 'bold' }}
          >
            More details
          </Button>
          <Collapse in={detailsVisible}>
            <Typography sx={{ marginLeft: '28px' }} variant="body2">
              {MoreDetails}
            </Typography>
          </Collapse>
        </Stack>
      )}
    </MuiAlert>
  );
});
